import { useCallback, useMemo, useState } from "react";

import { BackgroundProps, Box, ColorProps, Flex } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { InventoryChangeReason } from "__graphql__/types";
import OutboundDetails from "flows/Inbound/components/OutboundDetails";
import { OutboundItemRemovalWarningModal } from "flows/Inbound/components/OutboundItemRemovalWarningModal";
import OutboundPhotoUpload from "flows/Inbound/components/OutboundPhotoUpload";
import OutboundReason, { getOutboundGroup } from "flows/Inbound/components/OutboundReason";
import {
  AdditionalOutboundReason,
  StockUpdatePlan,
  ProductComplaintMetadata,
  DeliveryType,
} from "flows/Inbound/stores/inboundStore/types";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { getNegativeTotalForReasons, getTotalForReasons } from "flows/Inbound/utils/helpers";
import { Page } from "shared/components/Page";
import { BottomTabBarContainer } from "ui/BottomTabBarContainer";
import { Button } from "ui/Button/Button";
import { NavigationHeaderShelfNumber } from "ui/NavigationHeaderShelfNumber/NavigationHeaderShelfNumber";
import { ProductInformation } from "ui/ProductInformation/ProductInformation";
import { HeaderM, TitleS } from "ui/Typography/Typography";

type OutboundCounterProps = {
  numberColor?: ColorProps["color"];
  bgColor?: BackgroundProps["bg"];
  value: number;
  title: string;
  countDataTestId?: string;
};

type InboundOutboundPageProps = {
  deliveryType: DeliveryType | null;
  outboundSku: string;
  shelfLetter: string | null;
  shelfNumber: string | null;
  productSku: string;
  productName: string | null;
  productImageUrl: string | null;
  stockUpdatePlan: StockUpdatePlan[];
  isStockUpdating: boolean;
  updateStock: (
    sku: string,
    quantitiesByReason: StockUpdatePlan[],
    productComplaintMetadata?: ProductComplaintMetadata,
  ) => void;
  returnToInboundHomePage: () => void;
};

function OutboundCounter({
  numberColor,
  bgColor,
  value,
  title,
  countDataTestId,
}: OutboundCounterProps) {
  return (
    <Flex direction="column" alignItems="center">
      <TitleS color="grey.600">{title}</TitleS>
      <HeaderM
        data-testid={countDataTestId}
        paddingX="s100"
        borderRadius="8px"
        bg={bgColor}
        color={numberColor}
        mt="s50"
      >
        {value}
      </HeaderM>
    </Flex>
  );
}

export function InboundOutboundPage({
  deliveryType,
  outboundSku,
  shelfLetter,
  shelfNumber,
  productSku,
  productName,
  productImageUrl,
  stockUpdatePlan,
  isStockUpdating,
  updateStock,
  returnToInboundHomePage,
}: InboundOutboundPageProps) {
  const intl = useIntl();
  const { inboundUIState, setInboundUIState } = useInboundStore(
    useShallow((state) => ({
      inboundUIState: state.inboundUIState,
      setInboundUIState: state.setInboundUIState,
    })),
  );

  const initialCountForReason = useCallback(
    (reason: InventoryChangeReason) => {
      return stockUpdatePlan?.find((plan: any) => plan.reason === reason)?.quantityDelta ?? 0;
    },
    [stockUpdatePlan],
  );

  const initialOutboundCount = useMemo(
    () =>
      getNegativeTotalForReasons(stockUpdatePlan, [
        InventoryChangeReason.outbound_delivery_product_damaged,
        InventoryChangeReason.outbound_delivery_product_expired,
      ]),
    [stockUpdatePlan],
  );

  const [outboundCount, setOutboundCount] = useState(initialOutboundCount);
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [lotNumber, setLotNumber] = useState<string>("");
  const [detailsSelected, setDetailsSelected] = useState<"production_code" | "bbd_date" | null>(
    null,
  );
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [selectedSubReason, setSelectedSubReason] = useState<AdditionalOutboundReason | null>(null);

  const outboundGroup = getOutboundGroup(selectedSubReason);

  const handleDetailsSelect = useCallback((value: "production_code" | "bbd_date") => {
    setDetailsSelected(value);
    setExpirationDate("");
    setLotNumber("");
  }, []);

  const inboundCount = useMemo(
    () => getTotalForReasons(stockUpdatePlan, [InventoryChangeReason.inbound_goods_received]),
    [stockUpdatePlan],
  );

  const isIncreaseBtnDisabled = outboundCount >= inboundCount;
  const newStockValue = inboundCount - outboundCount;

  const confirmOutboundChoices = useCallback(() => {
    if (!outboundGroup) return;

    const reason =
      outboundGroup === "damaged"
        ? InventoryChangeReason.outbound_delivery_product_damaged
        : InventoryChangeReason.outbound_delivery_product_expired;

    const quantitiesByReason = [{ reason, quantityDelta: -outboundCount }];

    const shouldShowPartiallyOutboundedAnimation =
      initialCountForReason(reason) === 0 &&
      outboundCount > 0 &&
      outboundCount < initialCountForReason(InventoryChangeReason.inbound_goods_received);

    setInboundUIState({
      isPartialOutboundAnimationVisible: shouldShowPartiallyOutboundedAnimation
        ? outboundSku
        : null,
      isOutboundItemRemovalWarningModalVisible: false,
    });
    const productComplaintMetadata: ProductComplaintMetadata = {
      lotNumber,
      expirationDate,
      imageUrls,
      additionalOutboundReason: selectedSubReason,
    };
    if (outboundCount > 0)
      updateStock(outboundSku || "", quantitiesByReason, productComplaintMetadata);
    if (!isStockUpdating) returnToInboundHomePage();
  }, [
    outboundCount,
    outboundGroup,
    initialCountForReason,
    isStockUpdating,
    outboundSku,
    returnToInboundHomePage,
    setInboundUIState,
    updateStock,
    lotNumber,
    expirationDate,
    imageUrls,
    selectedSubReason,
  ]);

  const handleOnConfirm = useCallback(() => {
    if (newStockValue === 0) {
      setInboundUIState({ isOutboundItemRemovalWarningModalVisible: true });
    } else {
      confirmOutboundChoices();
    }
  }, [confirmOutboundChoices, newStockValue, setInboundUIState]);

  const isConfirmButtonDisabled = useMemo(() => {
    if (outboundCount === 0) return true;
    if (selectedSubReason === null) return true;
    if (deliveryType !== DeliveryType.REWE) {
      return (
        detailsSelected === null ||
        (detailsSelected === "bbd_date" && expirationDate === "") ||
        (detailsSelected === "production_code" && lotNumber === "") ||
        imageUrls.length === 0
      );
    }
    return false;
  }, [
    deliveryType,
    imageUrls.length,
    detailsSelected,
    expirationDate,
    lotNumber,
    outboundCount,
    selectedSubReason,
  ]);

  return (
    <Page isFull isBgGrey h="100%">
      <NavigationHeaderShelfNumber
        title={intl.formatMessage({ id: "pages.inbound.inbound-outbound.title" })}
        shelfLetter={shelfLetter}
        shelfNumber={shelfNumber}
        onClickGoBack={returnToInboundHomePage}
      />
      <Flex
        direction="column"
        w="100%"
        h="100%"
        flex={1}
        justify="flex-start"
        bg="grey.100"
        overflowX="scroll"
      >
        <Box bg="white" w="full">
          <ProductInformation
            name={productName ?? ""}
            imageUrl={productImageUrl}
            sku={productSku}
            variantSize="s"
            p="s200"
            borderRadius="0"
          />
        </Box>
        <Flex direction="column" w="100%" p="s200" gap="s200">
          <Flex
            alignItems="center"
            justifyContent="space-evenly"
            borderRadius="8px"
            bg="white"
            w="100%"
            p="s200"
          >
            <OutboundCounter
              countDataTestId="counted-value"
              title={intl.formatMessage({ id: "pages.inbound.inbound-outbound.counted-title" })}
              value={inboundCount}
            />
            <OutboundCounter
              countDataTestId="outbounded-value"
              title={intl.formatMessage({
                id: "pages.inbound.inbound-outbound.outbounded-title",
              })}
              value={outboundCount * -1}
              numberColor="purple.500"
            />
            <OutboundCounter
              countDataTestId="new-stock-value"
              title={intl.formatMessage({ id: "pages.inbound.inbound-outbound.new-stock-title" })}
              value={newStockValue}
              numberColor="pinkFlink.500"
              bgColor="pinkFlink.100"
            />
          </Flex>

          <OutboundReason
            outboundCount={outboundCount}
            setOutboundCount={setOutboundCount}
            isIncreaseBtnDisabled={isIncreaseBtnDisabled}
            selectedSubReason={selectedSubReason}
            setSelectedSubReason={setSelectedSubReason}
          />
          {deliveryType !== DeliveryType.REWE && selectedSubReason !== null && (
            <>
              <OutboundDetails
                onDetailsSelect={handleDetailsSelect}
                onBBDChange={setExpirationDate}
                onProductionCodeChange={setLotNumber}
              />
              <OutboundPhotoUpload onPhotosChanged={setImageUrls} />
            </>
          )}
        </Flex>
      </Flex>
      <BottomTabBarContainer
        w="100%"
        px="s200"
        bgColor="white"
        borderTopWidth="0.5px"
        borderTopColor="grey.200"
        align="center"
      >
        <Button
          isFullWidth
          disabled={isConfirmButtonDisabled}
          onClick={handleOnConfirm}
          isLoading={isStockUpdating}
          data-testid="outbound-confirm"
        >
          <FormattedMessage id="pages.inbound.inbound-outbound.confirm-button" />
        </Button>
      </BottomTabBarContainer>
      <OutboundItemRemovalWarningModal
        isOpen={inboundUIState.isOutboundItemRemovalWarningModalVisible}
        onClickYes={confirmOutboundChoices}
        onClickNo={() => setInboundUIState({ isOutboundItemRemovalWarningModalVisible: false })}
      />
    </Page>
  );
}
